import { Fragment, FC } from 'react';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { Flex } from 'components/Flex';
import {
  BKJBadge,
  BKJButton,
  BKJIconTooltip,
  BKJLoadingSpinner,
  BKJThemeColorType,
} from '@bookjane2/bookjane-design-library';
import {
  JaneName,
  Text,
  H4,
  ValueText,
  LabelText,
  GeneralJaneWrapper,
  JaneAvatarWrapper,
  ColorDot,
  LoadingWrapper,
  PendingSignUpText,
  NonAppUserWrapper,
  EllipsisText,
  FieldWrapper,
  SectionWrapper,
  HeadSection,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/views/JaneModal/JaneDetailsView/GeneralTab/GeneralTab.styled';
import { useTranslation } from 'react-i18next';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import JaneMenu from 'modals/JaneDetailsModal/JaneMenu';
import { useFetchJaneByIdDataContext } from 'modals/JaneDetailsModal/FetchJaneById.data.context';
import { Jane } from 'types/graphql';
import { TeamMemberJaneAvatar } from 'components/TeamMemberJaneAvatar';
import { TooltipWrapper } from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.styled';
import { FeatureGuard } from 'guards/FeatureGuard';
import { useSelector } from 'react-redux';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { isUserReadOnlySelector } from 'store/selectors/userSelectors/isUserReadOnlySelector';
import { SessionService } from 'services';
import { startCase } from 'utils/startCase';
import { shiftModalGeneralTabValueTextMarginLeft } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/views/JaneModal/JaneDetailsView/GeneralTab/GeneralTab.constants';
import i18next from 'i18next';
import {
  removePositionModalRoutingService,
  useRemovePositionModalUIContext,
} from 'pages/Settings/pages/LocationSettings/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { StarRating } from 'components/StarRating';
import { MountTransition } from 'routes';
import {
  secondaryLocationCallOutFilter,
  secondaryLocationSchedulingFilter,
} from 'modals/JaneDetailsModal/views/JaneDetailsView/GeneralTab/GeneralTab.constants';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationSchedulingFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';

export const GeneralTab: FC = () => {
  const { t } = useTranslation();
  const isUserAgency = useSelector(isUserAgencySelector);
  const isReadOnly = useSelector(isUserReadOnlySelector);

  const {
    data,
    dotColor,
    showDeactivateConfirmModal,
    setShowDeactivateConfirmModal,
    deactivateTeamMember,
    reactivateTeamMember,
  }: {
    data: Jane;
    dotColor: BKJThemeColorType;
    showDeactivateConfirmModal: boolean;
    setShowDeactivateConfirmModal: (showDeactivateConfirmModal: boolean) => void;
    deactivateTeamMember: () => void;
    reactivateTeamMember: () => void;
    resendInviteTeamMember: () => void;
    showPendingSignUpModal: boolean;
    setShowPendingSignUpModal: (showPendingSignUpModal: boolean) => void;
  } = useFetchJaneByIdDataContext();

  const { locationId } = useRemovePositionModalUIContext();

  const isMultiLocationSchedulingEnabled = useSelector(
    isMultiLocationSchedulingFeatureEnabledSelector(Number(locationId)),
  );

  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(locationId)),
  );
  const isMultiLocationCallOutEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(locationId)),
  );

  if (!data)
    return (
      <LoadingWrapper>
        <BKJLoadingSpinner variant="Default" />
      </LoadingWrapper>
    );

  const {
    avatarUrls = { origin: '' },
    accessStatus = { name: '' },
    primaryPosition = { details: { name: '' } },
    customerReviews = { averageRating: '', count: '' },
    communityName = '',
    primaryCommunity = { displayId: '' },
    usersCommunities = [],
    secondaryPositions = [],
    shorthandName = '',
    pendingSignup = false,
    isMobile = false,
  } = data;
  const primaryCommunityId = primaryCommunity?.displayId;

  const isJanePrimaryLocationSame = locationId === primaryCommunityId;
  const showEdit = isJanePrimaryLocationSame || SessionService.assertUserType('Agency');
  const averageRating = !customerReviews?.count ? 0 : Number(customerReviews?.averageRating) || 0;
  return (
    <Fragment>
      <BKJCenteredDialog
        isOpen={showDeactivateConfirmModal}
        variant="Danger"
        title={t('team_member:DEACTIVATE_TEAM_MEMBER')}
        text={
          isUserAgency
            ? t('team_member:THIS_WILL_BLOCK_THE_TEAM_MEMBER_FROM_ACCESSING')
            : t('team_member:BEFORE_DEACTIVATING_THE_TEAM_MEMBER')
        }
        cancelLabel={i18next.t('auth:CANCEL')}
        actionLabel={i18next.t('team_member:DEACTIVATE')}
        onAction={deactivateTeamMember}
        onCancel={() => setShowDeactivateConfirmModal(false)}
        onClose={() => setShowDeactivateConfirmModal(false)}
      />
      <Flex flexDirection="column" childMarginBottom="40px" flex="1">
        <MountTransition>
          <HeadSection>
            <JaneAvatarWrapper>
              <TeamMemberJaneAvatar
                url={avatarUrls?.origin}
                status={accessStatus?.name}
                size={112}
                pendingSignup={pendingSignup}
              />
            </JaneAvatarWrapper>
            <Flex justifyContent="space-between" width="100%">
              <Flex flexDirection="column" gap="15px" width="100%">
                <Flex justifyContent="space-between">
                  <JaneName>{shorthandName}</JaneName>
                  {showEdit && (
                    <FeatureGuard NOT type="FEATURE_TYPE_ACCUSHIELD_SYNCING">
                      <JaneMenu
                        disabled={false}
                        setShowDeactivateConfirmModal={setShowDeactivateConfirmModal}
                        accessStatusName={accessStatus?.name}
                        reactivateTeamMember={reactivateTeamMember}
                      />
                    </FeatureGuard>
                  )}
                </Flex>
                <Flex alignItems="center" margin="0 0 0 4px">
                  <ColorDot color={dotColor}>•</ColorDot>
                  <Text>
                    {accessStatus.name === 'blocked'
                      ? t('team_member:Deactivated')
                      : t(`team_member:${startCase(accessStatus?.name)}`)}
                  </Text>
                  {pendingSignup && accessStatus?.name === 'approved' && (
                    <PendingSignUpText>
                      ({i18next.t('team_member:PENDING_MOBILE_APP_SIGN_UP')})
                    </PendingSignUpText>
                  )}
                </Flex>
                <EllipsisText>{primaryPosition?.details?.name}</EllipsisText>
                {isMobile && (
                  <NonAppUserWrapper>
                    <Flex>
                      <BKJBadge variant="Grey"> {t('team_member:Non_App_User')}</BKJBadge>
                      <BKJIconTooltip iconName="Info" placement="bottom">
                        <TooltipWrapper>
                          {i18next.t('team_member:STAFF_WILL_RECEIVE_AUTOMATED_PHONE')}
                        </TooltipWrapper>
                      </BKJIconTooltip>
                    </Flex>
                  </NonAppUserWrapper>
                )}
                <div>
                  <StarRating rating={averageRating} />
                </div>
              </Flex>
            </Flex>
          </HeadSection>
          <GeneralJaneWrapper>
            <SectionWrapper>
              <H4>{t('team_member:GENERAL_PROFILE')}</H4>
              <FieldWrapper>
                <LabelText>{t('team_member:PRIMARY_LOCATION')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {communityName}
                </ValueText>
              </FieldWrapper>
              <Fragment>
                {isMultiLocationSchedulingEnabled && (
                  <FieldWrapper>
                    <LabelText>{t('team_member:SECONDARY_LOCATIONS_SCHEDULING')}</LabelText>
                    {usersCommunities?.filter(secondaryLocationSchedulingFilter)?.length ? (
                      usersCommunities
                        .filter(secondaryLocationSchedulingFilter)
                        .map((secondaryLocation) => (
                          <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                            {secondaryLocation.community.name}
                          </ValueText>
                        ))
                    ) : (
                      <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>-</ValueText>
                    )}
                  </FieldWrapper>
                )}
                {(isMultiLocationTieringEnabled || isMultiLocationCallOutEnabled) && (
                  <FieldWrapper>
                    <LabelText>{t('team_member:SECONDARY_LOCATIONS_CALL_OUT')}</LabelText>
                    {usersCommunities?.filter(secondaryLocationCallOutFilter)?.length ? (
                      usersCommunities
                        .filter(secondaryLocationCallOutFilter)
                        .map((secondaryLocation) => (
                          <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                            {secondaryLocation.community.name}
                          </ValueText>
                        ))
                    ) : (
                      <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>-</ValueText>
                    )}
                  </FieldWrapper>
                )}
              </Fragment>
              <FieldWrapper>
                <LabelText>{t('team_member:PRIMARY_POSITION')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {primaryPosition?.details?.name}
                </ValueText>
              </FieldWrapper>
              <FieldWrapper>
                <LabelText>{t('team_member:SECONDARY_POSITIONS')}</LabelText>
                {secondaryPositions.length > 0 ? (
                  secondaryPositions.map((position, key) => (
                    <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft} key={key}>
                      {position?.details?.name}
                    </ValueText>
                  ))
                ) : (
                  <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>-</ValueText>
                )}
              </FieldWrapper>
            </SectionWrapper>
          </GeneralJaneWrapper>
        </MountTransition>
      </Flex>
      {!isReadOnly && (
        <BKJSideModalFooter gap="8px" justifyContent="end">
          <BKJButton
            variant="PurpleSolid"
            width="180px"
            onClick={() =>
              removePositionModalRoutingService.open({ janeId: data?.id }, 'EditJaneView')
            }
            type="button"
          >
            {t('team_member:EDIT')}
          </BKJButton>
        </BKJSideModalFooter>
      )}
    </Fragment>
  );
};
